/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * useOrganizerOrg
 * ---------------
 * Which organization the signed-in organizer authors events as.
 *
 * The admin form asks an admin to pick this from every organization on the
 * platform. An organizer must not be asked: they belong to one, and picking
 * another one is not a thing they are allowed to do. The AthloHive pack seeds
 * an `Organization` RLS group with `rls_scope='SELF'` and `organization` among
 * its tables, so a plain fetch already returns only the rows this user owns —
 * the filtering is the database's job, not a query parameter we could forget.
 *
 * Three outcomes, all of which a caller must handle:
 *   • one row   — the normal case; used silently
 *   • many rows — `needsChoice`, with `options` for a picker
 *   • no rows   — `isMissing`; the account is not linked to an organization yet
 *
 * `isMissing` is deliberately false while loading and false on error. "You have
 * no organization" is a specific, actionable claim, and making it because a
 * request timed out sends the organizer to ask for something they already have.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { readLookup, toOptions } from '../components/athlohive/lookupOptions';
import { retrieve } from '../utils/storage_helper';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

export default function useOrganizerOrg() {
  // Set only when there is a genuine choice; otherwise the resolved row wins.
  const [chosenId, setChosenId] = useState(null);

  const query = useQuery({
    queryKey: ['organizerOrganization'],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall('/arcee/application/table/fetch/', {
        method: 'POST',
        signal,
        body: JSON.stringify({
          table_name: 'organization',
          organization_id: Number(envConfig.organizationId),
          application_id: Number(envConfig.applicationId),
          page_size: 50,
        }),
      });
      if (!resp.ok) throw new Error('Could not load your organization.');
      return readLookup(await resp.json());
    },
    staleTime: 5 * 60 * 1000,
  });

  const options = useMemo(() => toOptions(query.data), [query.data]);
  const rows = query.data?.rows || [];
  const settled = !query.isLoading && !query.isError;

  /**
   * The organizer's own organization, out of whatever the fetch returned.
   *
   * `created_by_id` holds the ApplicationUser id of whoever created the row,
   * and `userData.user_id` — despite the name — is that same ApplicationUser
   * id, not the auth_user id. So an exact match on the two is the ownership
   * link, and it is the only one available: there is no membership table
   * joining a user to an organization.
   *
   * Taking the FIRST row instead, as this once did, is wrong whenever the fetch
   * returns more than one. It currently returns ten: `organization` is a
   * public-visibility table, and a signed-in user with no row-level-security
   * group assignment resolves to GLOBAL scope, so no row filter is applied at
   * all. Row one is somebody else's, and the wizard would have authored events
   * under it.
   *
   * The single-row fallback is what makes this correct once that scoping is
   * configured: RLS then returns exactly the organizer's own row, and a row
   * whose `created_by_id` is null — seed data, or a row a platform admin
   * created on their behalf — is still theirs.
   */
  const owned = useMemo(() => {
    if (!settled || !rows.length) return null;
    const appUserId = retrieve('userData')?.user_id;
    const mine = appUserId == null
      ? []
      : rows.filter((row) => String(row.created_by_id) === String(appUserId));
    if (mine.length) return mine[0];
    // Scoping already narrowed it to one: trust it, whoever created it.
    if (rows.length === 1) return rows[0];
    return null;
  }, [settled, rows]);

  const organizationId = owned ? (chosenId ?? owned.id) : null;

  const organizationName = useMemo(() => {
    if (organizationId == null) return '';
    const match = options.find((option) => String(option.value) === String(organizationId));
    return match ? match.label : '';
  }, [options, organizationId]);

  return {
    organizationId,
    organizationName,
    options,
    // Kept so a caller can still tell the ambiguous case apart, but the wizard
    // no longer offers a picker: an organizer authors as their own
    // organization, and choosing somebody else's is not a thing they may do.
    needsChoice: false,
    // Rows came back, but none of them are this organizer's. Distinct from "the
    // fetch failed" and from "there are no organizations at all" — all three
    // need different words.
    isMissing: settled && !owned,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    chooseOrganization: setChosenId,
  };
}
